import { ZoomPhoneRecordingApiService } from '@/api/user/resources/zoom_phone_recording';
import store from '@/store/index';
import { ZoomPhoneState } from '@/store/types';
import { TCallConnectedEventData, TCallEndedEventData, TCallRingingEventData, TEventData, TCallLogCompletedEventData, TCallRecordingCompletedEventData } from './zoom_phone_types';

// イベントを拡張するためのカスタムハンドラ
let customCallConnectedHandler: ((data: TCallConnectedEventData) => void) | null = null;
let customCallEndedHandler: ((data: TCallEndedEventData) => void) | null = null;

// 設定系

const zoomPhoneContainer = () => document.querySelector<HTMLIFrameElement>('iframe#zoom-embeddable-phone-iframe');
const zoomPhone = () => zoomPhoneContainer().contentWindow;

const initZoomPhone = (): void => {
  window.removeEventListener('message', handleEvents);
  window.addEventListener('message', handleEvents);
};

const removeZoomPhone = (): void => {
  window.removeEventListener('message', handleEvents);
};

/**
 * 発着信
 */
const handleCallRinging = (data: TCallRingingEventData): void => {
  const { callId: id, direction, dateTime: startedAt } = data;
  store.dispatch('zoomPhone/setCallStartedInfoAction', { id, direction, startedAt });
};

/**
 * call connected
 */
const handleCallConnected = (data: TCallConnectedEventData): void => {
  const { callId: id, direction, dateTime: connectedAt } = data;
  store.dispatch('zoomPhone/setCallConnectedInfoAction', { id, direction, connectedAt });

  // 拡張用カスタムハンドラ実行
  if (customCallConnectedHandler) {
    customCallConnectedHandler(data);
  }
};

/**
 * call終了
 */
const handleCallEnded = (data: TCallEndedEventData): void => {
  const { callId: id, direction, dateTime: endedAt, result } = data;
  store.dispatch('zoomPhone/setCallEndedInfoAction', { id, direction, endedAt, result });

  // 拡張用カスタムハンドラ実行
  if (customCallEndedHandler) {
    customCallEndedHandler(data);
  }
};

/**
 * zp-call-ended-event 取りこぼし時のフォールバック
 *
 * NOTE: 本来 zp-call-ended-event で isCalling=false になるが、取りこぼされると
 *       isCalling=true のまま放置され、コール結果フォームが保存できなくなる。
 *       後続の zp-call-log-completed-event / zp-call-recording-completed-event を
 *       フォールバックトリガとして isCalling=false のみ設定する。
 *       通常ケース（zp-call-ended-event 受信済み）では isCalling は既に false のため実質何もしない。
 *       別コールのイベント取りこぼしで現在のコール状態を壊さないよう、callId の一致を確認する。
 */
const fallbackSetCallEnded = (data: TCallLogCompletedEventData | TCallRecordingCompletedEventData): void => {
  const currentCallInfo = store.getters['zoomPhone/callInfo'] as ZoomPhoneState['callInfo'];
  if (currentCallInfo.id !== data.callId) return;
  store.dispatch('zoomPhone/setCallEndedFallbackAction');
};

/**
 * コールログ作成完了
 */
const handleCallLogCompleted = (data: TCallLogCompletedEventData): void => {
  fallbackSetCallEnded(data);
};

/**
 * 音声ファイル準備完了
 */
const handleCallRecordingCompleted = async (data: TCallRecordingCompletedEventData) => {
  fallbackSetCallEnded(data);

  const isVaApp = store.getters['user/currentUser']?.appType === 'voice_analytics';
  if (isVaApp) return;

  const { callId } = data;
  const zoomPhoneRecordingApiService = new ZoomPhoneRecordingApiService();
  await zoomPhoneRecordingApiService.downloadZoomPhoneRecording({
    request: {
      downloadZoomPhoneRecordingRequest: {
        callId,
      },
    },
  });
};

/**
 * event handle（分岐）
 */
const handleEvents = (e: MessageEvent<TEventData>) => {
  if (!e.data) return;

  const { type, data } = e.data;
  switch (type) {
    case 'zp-call-ringing-event': // 発信・着信時
      handleCallRinging(data as TCallRingingEventData);
      break;
    case 'zp-call-connected-event': // call connected
      handleCallConnected(data as TCallConnectedEventData);
      break;
    case 'zp-call-ended-event': // call終了
      handleCallEnded(data as TCallEndedEventData);
      break;
    case 'zp-call-log-completed-event': // コールログ作成完了
      handleCallLogCompleted(data as TCallLogCompletedEventData);
      break;
    case 'zp-call-recording-completed-event': // 音声ファイル準備完了
      void handleCallRecordingCompleted(data as TCallRecordingCompletedEventData);
      break;
  }
};

// 呼び出し

/**
 * 発信
 */
const makeCall = (phoneNumber: string, onFailed: () => void): void => {
  // NOTE: この時点で「通話中」にする
  store.dispatch('zoomPhone/setOutboundCallingAction');

  // NOTE: 5秒経過しても発信がされない場合は、失敗とみなす。
  const timeout = setTimeout(() => {
    if (store.getters['zoomPhone/hasCallInfo']) return;
    store.dispatch('zoomPhone/resetOutboundCallingAction');
    onFailed();
  }, 5000);

  // NOTE: これが必要となるのは、5秒以内に「発信 -> 通話完了 -> 発信」が行われたケースくらいと思われる。
  //       そのため現実的にはなくてもよさそうではある。
  //       ただし、一応ロジック上は存在するパターンのため、いれておく。
  window.addEventListener('message', (e: MessageEvent<TEventData>) => {
    if (e.data.type === 'zp-call-ringing-event') {
      clearTimeout(timeout);
    }
  }, { once: true });

  zoomPhone().postMessage({
    type: 'zp-make-call',
    data: { number: phoneNumber },
  }, 'https://applications.zoom.us');
};

/**
 * 拡張用カスタムハンドラ設定するsetterの提供
 */
const setCustomCallConnectedHandler = (
  handler: ((data: TCallConnectedEventData) => void) | null,
): void => {
  customCallConnectedHandler = handler;
};

const setCustomCallEndedHandler = (
  handler: ((data: TCallEndedEventData) => void) | null,
): void => {
  customCallEndedHandler = handler;
};

export {
  initZoomPhone,
  removeZoomPhone,
  makeCall,
  setCustomCallConnectedHandler,
  setCustomCallEndedHandler,
};
